import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {MessengerFlags} from './messenger-flags';
import {MessengerPortBacklog, routedPort} from './messenger-port';
import {ConversationCall, ConversationHit, ConversationScope, MutedUntil} from './messenger.models';
import {MessengerConversationOrganisationReal} from './conversation-organisation.real';
import {MessengerConversationOrganisationFixture} from './conversation-organisation.fixture';

/**
 * THE CONVERSATION ORGANISATION PORT (ITP-E-194-02): finding, filtering and organising conversations in the list.
 * Records are keyed by conversation id; a reading method answers again whenever its marks change.
 */
@Injectable({
  providedIn: 'root',
  useFactory: () => routedPort<MessengerConversationOrganisationPort>(inject(MessengerConversationOrganisationReal),
    inject(MessengerConversationOrganisationFixture), MESSENGER_CONVERSATION_ORGANISATION_BACKLOG, inject(MessengerFlags))
})
export abstract class MessengerConversationOrganisationPort {
  /** B-01: a `search` argument on the conversation list query, through the visibility predicate. */
  abstract search(text: string): Observable<ConversationHit[]>;

  /** B-02: `hasUnreadMention` on the list item. */
  abstract unreadMentions(conversationIds: string[]): Observable<Record<string, boolean>>;

  /** B-03: a calls query over `CallInvitation` for one conversation, newest first. */
  abstract callHistory(conversationId: string): Observable<ConversationCall[]>;

  /** B-04: Mission and Project conversation sources with their link keys. */
  abstract scopes(conversationIds: string[]): Observable<Record<string, ConversationScope>>;

  /** B-05: `isPinned` on the list item. */
  abstract pinned(conversationIds: string[]): Observable<Record<string, boolean>>;

  /** B-05: `PinConversationCommand`. */
  abstract setPinned(conversationId: string, pinned: boolean): Observable<void>;

  /** B-06: `mutedUntil` on the list item; absent when not muted. */
  abstract mutedUntil(conversationIds: string[]): Observable<Record<string, MutedUntil>>;

  /** B-06: `MuteConversationCommand(until)`; null unmutes. */
  abstract mute(conversationId: string, until: MutedUntil | null): Observable<void>;

  /** B-07: `isArchived` on the list item. */
  abstract archived(conversationIds: string[]): Observable<Record<string, boolean>>;

  /** B-07: `ArchiveConversationCommand`. */
  abstract setArchived(conversationId: string, archived: boolean): Observable<void>;

  /** B-08: `markedUnread` on the list item. */
  abstract markedUnread(conversationIds: string[]): Observable<Record<string, boolean>>;

  /** B-08: `MarkConversationUnreadCommand`. */
  abstract setMarkedUnread(conversationId: string, unread: boolean): Observable<void>;
}

export const MESSENGER_CONVERSATION_ORGANISATION_BACKLOG: MessengerPortBacklog<MessengerConversationOrganisationPort> = {
  search: 'B-01',
  unreadMentions: 'B-02',
  callHistory: 'B-03',
  scopes: 'B-04',
  pinned: 'B-05',
  setPinned: 'B-05',
  mutedUntil: 'B-06',
  mute: 'B-06',
  archived: 'B-07',
  setArchived: 'B-07',
  markedUnread: 'B-08',
  setMarkedUnread: 'B-08'
};
